import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuestionnaire } from '../contexts/QuestionnaireContext';
import Question from '../components/questionnaire/Question';
import ProgressBar from '../components/questionnaire/ProgressBar';
import ResultsDisplay from '../components/questionnaire/ResultsDisplay';
import type { StatusResult } from '../types';

export default function Questionnaire() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    state,
    currentQuestion,
    currentOptions,
    progress,
    isLoading,
    questions,
    answerQuestion,
    goBack,
    reset,
    getResult,
  } = useQuestionnaire();

  const [hasAcknowledged, setHasAcknowledged] = useState(false);
  const [isLoadingResult, setIsLoadingResult] = useState(false);
  const [result, setResult] = useState<StatusResult | null>(null);

  // Handle questionnaire completion
  React.useEffect(() => {
    async function fetchResult() {
      if (state.isComplete && !result && !isLoadingResult) {
        setIsLoadingResult(true);
        const res = await getResult();
        setResult(res);
        setIsLoadingResult(false);
      }
    }
    fetchResult();
  }, [state.isComplete, result, isLoadingResult, getResult]);

  const handleAnswer = (answerId: string, value?: string | number | Date) => {
    answerQuestion(answerId, value);
  };

  const handleStartOver = () => {
    reset();
    setResult(null);
    setHasAcknowledged(false);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Show results
  if (state.isComplete && result) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <ResultsDisplay result={result} onStartOver={handleStartOver} />
      </div>
    );
  }

  // Loading results
  if (state.isComplete && isLoadingResult) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Calculating your results...</p>
        </div>
      </div>
    );
  }

  // Acknowledgment screen
  if (!hasAcknowledged) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="card">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">
            {t('questionnaire.confirmStart.title')}
          </h1>

          <div className="space-y-4 text-gray-600 mb-8">
            <p>{t('questionnaire.confirmStart.content')}</p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-medium text-blue-900 mb-2">Privacy Notice</h3>
              <p className="text-sm text-blue-800">
                {t('questionnaire.confirmStart.privacy')}
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h3 className="font-medium text-amber-900 mb-2">Legal Notice</h3>
              <p className="text-sm text-amber-800">
                {t('questionnaire.confirmStart.legal')}
              </p>
            </div>
          </div>

          <label className="flex items-start gap-3 cursor-pointer mb-6">
            <input
              type="checkbox"
              checked={hasAcknowledged}
              onChange={(e) => setHasAcknowledged(e.target.checked)}
              className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500 mt-0.5"
            />
            <span className="text-gray-700">
              {t('questionnaire.confirmStart.acknowledge')}
            </span>
          </label>

          <div className="flex gap-4">
            <button
              onClick={() => navigate('/')}
              className="btn-secondary flex-1"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={() => setHasAcknowledged(true)}
              disabled={!hasAcknowledged}
              className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('questionnaire.confirmStart.begin')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main questionnaire
  if (!currentQuestion) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">No questions available.</p>
          <button onClick={() => navigate('/')} className="btn-primary mt-4">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Progress */}
      <ProgressBar
        progress={progress}
        currentStep={state.history.length + 1}
        totalSteps={questions.length}
      />

      {/* Question */}
      <div className="card">
        <Question
          question={currentQuestion}
          options={currentOptions}
          onAnswer={handleAnswer}
          selectedAnswer={state.answers.find(a => a.questionId === currentQuestion.id)?.answerId}
        />

        {/* Navigation */}
        <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-100">
          <button
            onClick={goBack}
            disabled={state.history.length === 0}
            className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('common.back')}
          </button>

          <span className="text-sm text-gray-500">
            {t('questionnaire.disclaimer')}
          </span>
        </div>
      </div>
    </div>
  );
}
